import { useState } from 'react';
import SectionHeading from '@/pages/home/components/SectionHeading';
import { hotComments, hotTopics } from '@/mocks/home';

type Comment = (typeof hotComments)[number];

const trendMap: Record<string, { icon: string; color: string }> = {
  up: { icon: 'ri-arrow-up-line', color: 'text-primary-600' },
  down: { icon: 'ri-arrow-down-line', color: 'text-foreground-400' },
  flat: { icon: 'ri-subtract-line', color: 'text-foreground-400' },
};

function CommentCard({ comment }: { comment: Comment }) {
  const [liked, setLiked] = useState(false);

  return (
    <div className="flex items-start gap-4 border-b border-background-200/70 py-5 last:border-b-0">
      <div className="h-11 w-11 shrink-0 overflow-hidden rounded-full border border-background-200">
        <img
          src={comment.avatar}
          alt={`${comment.user} 的头像`}
          title="热议现场 评论"
          className="h-full w-full object-cover"
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-label text-sm font-medium text-foreground-900">
            {comment.user}
          </span>
          <span className="font-label text-xs text-foreground-400">{comment.time}</span>
        </div>
        <p className="mt-2 font-body text-sm leading-relaxed text-foreground-700">
          {comment.text}
        </p>
        <div className="mt-3 flex items-center gap-5 font-label text-xs text-foreground-500">
          <button
            type="button"
            onClick={() => setLiked((v) => !v)}
            className={`flex items-center gap-1.5 transition-colors ${
              liked ? 'text-primary-600' : 'hover:text-primary-600'
            }`}
          >
            <i className={liked ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} />
            {comment.likes + (liked ? 1 : 0)}
          </button>
          <button
            type="button"
            className="flex items-center gap-1.5 transition-colors hover:text-primary-600"
          >
            <i className="ri-chat-3-line" />
            {comment.replies} 条回复
          </button>
          <button
            type="button"
            className="ml-auto flex items-center gap-1.5 transition-colors hover:text-primary-600"
          >
            分享
          </button>
        </div>
      </div>
    </div>
  );
}

export default function HotTalk() {
  const [draft, setDraft] = useState('');
  const [notice, setNotice] = useState('');
  const [myComments, setMyComments] = useState<string[]>([]);

  const handlePublish = () => {
    if (!draft.trim()) {
      setNotice('先写点什么，再发布你的看法。');
      return;
    }
    setMyComments((list) => [draft.trim(), ...list]);
    setDraft('');
    setNotice('已发布到本页演示评论区。');
  };

  return (
    <section id="talk" className="w-full px-5 py-16 md:px-8 md:py-24">
      <div className="mx-auto w-full max-w-[1320px]">
        <SectionHeading
          index="04"
          eyebrow="热议现场"
          title="评论区比正文还热闹的一晚"
          description="观点先放一边，这里只记录今天被点亮最多的几句话。"
        />

        <div className="mt-10 grid grid-cols-1 gap-8 md:mt-14 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <div className="rounded-lg border border-background-200/80 bg-background-100/60 p-5 md:p-6">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-lg font-bold text-foreground-950">
                  热议榜
                </h3>
                <span className="font-label text-xs tracking-editorial text-foreground-500">
                  更新于刚刚
                </span>
              </div>
              <ol className="mt-5 flex flex-col">
                {hotTopics.map((topic) => {
                  const trend = trendMap[topic.trend] ?? trendMap.flat;
                  return (
                    <li
                      key={topic.rank}
                      className="flex items-center gap-4 border-t border-background-200/70 py-4 first:border-t-0"
                    >
                      <span className="font-heading text-xl font-bold text-accent-600">
                        {topic.rank}
                      </span>
                      <span className="min-w-0 flex-1 font-body text-sm leading-snug text-foreground-800">
                        {topic.title}
                      </span>
                      <span className="flex shrink-0 items-center gap-1 font-label text-xs text-foreground-500">
                        {topic.heat}
                        <i className={`${trend.icon} ${trend.color}`} />
                      </span>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                  <i className="ri-user-smile-line text-lg" />
                </span>
                <div className="min-w-0 flex-1">
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value.slice(0, 200))}
                    rows={3}
                    placeholder="说说你对今日猛料的看法…"
                    className="w-full resize-none rounded-md border border-background-300/70 bg-background-50 p-3 font-body text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                  <div className="mt-3 flex items-center justify-between gap-4">
                    <span className="font-label text-xs text-foreground-400">
                      {draft.length}/200
                    </span>
                    <button
                      type="button"
                      onClick={handlePublish}
                      className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                    >
                      发布看法
                    </button>
                  </div>
                  {notice ? (
                    <p className="mt-3 font-label text-xs text-accent-700">{notice}</p>
                  ) : null}
                </div>
              </div>

              {myComments.length > 0 ? (
                <ul className="mt-5 flex flex-col gap-2">
                  {myComments.map((text, index) => (
                    <li
                      key={`${index}-${text}`}
                      className="rounded-md bg-accent-50 px-4 py-3 font-body text-sm text-foreground-700"
                    >
                      {text}
                    </li>
                  ))}
                </ul>
              ) : null}

              <div className="mt-6 border-t border-background-200/80 pt-2">
                {hotComments.map((comment) => (
                  <CommentCard key={comment.id} comment={comment} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}